import debounce from "lodash.debounce";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Chip } from "@material-tailwind/react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const SubCategoriesFilter = ({ subId, setSubId, subCategories, isScroll = false, isName = true }) => {
    const { i18n, t } = useTranslation();
    const sliderRef = useRef(null);

    // Debounced click handler
    const handleChip = useCallback(
        debounce((id) => {
            setSubId(id !== "All" ? id : null);
        }, 300),
        [setSubId]
    );

    // Scroll handler
    const scroll = (offset) => {
        if (sliderRef.current) {
            sliderRef.current.scrollBy({
                left: offset,
                behavior: "smooth",
            });
        }
    };

    // Render Subcategory Chips
    const renderChips = () => (
        <>
            <Chip
                value={t("all")}
                color="indigo"
                onClick={() => handleChip("All")}
                className={`${subId === null ? 'text-xs md:text-sm shadow-2xl bg-white border border-indigo-500 text-black' : ''} cursor-pointer`}
            />
            {
                isName
                    ?
                    subCategories?.map((cat) => (
                        <Chip
                            key={cat._id}
                            value={cat?.name?.[i18n.language]}
                            color="indigo"
                            onClick={() => handleChip(cat._id)}
                            className={`${subId === cat._id ? 'text-xs md:text-sm shadow-2xl bg-white border border-indigo-500 text-black' : ''} cursor-pointer`}
                        />
                    ))
                    :
                    subCategories?.map((cat) => (
                        <Chip
                            key={cat._id}
                            value={cat?.[i18n.language]}
                            color="indigo"
                            onClick={() => handleChip(cat._id)}
                            className={`${subId === cat._id ? 'text-xs md:text-sm shadow-2xl bg-white border border-indigo-500 text-black' : ''} cursor-pointer`}
                        />
                    ))}
        </>
    );

    return isScroll ? (
        <div className="relative mb-7">
            {/* Left Arrow (Visible on lg and above) */}
            <button
                className="absolute -left-4 top-1/2 border-2 border-gray-50 transform -translate-y-1/2 bg-indigo-500 text-white p-1 rounded-full shadow-lg hover:bg-indigo-600 z-10 hidden lg:block"
                onClick={() => scroll(-200)}
                aria-label="Scroll left"
            >
                <FaChevronLeft className="ltr:rotate-0 text-sm" />
            </button>

            {/* Scrollable Chip Container */}
            <div
                ref={sliderRef}
                className="flex gap-2 overflow-x-hidden scrollbar-hide scroll-smooth mt-2 py-2 lg:px-2 whitespace-nowrap"
            >
                {renderChips()}
            </div>

            {/* Right Arrow (Visible on lg and above) */}
            <button
                className="absolute -right-4 top-1/2 border-2 border-gray-50  transform -translate-y-1/2 bg-indigo-500 text-white p-1 rounded-full shadow-lg hover:bg-indigo-600 hidden lg:block"
                onClick={() => scroll(200)}
                aria-label="Scroll right"
            >
                <FaChevronRight className="text-sm" />
            </button>

            {/* Arrow icons for small and medium screens, positioned on the left side */}
            <div className="flex items-center ltr:justify-end rtl:justify-start mt-2 gap-2 lg:hidden">
                <button
                    className="bg-indigo-500 text-white p-1 rounded-full shadow-lg hover:bg-indigo-600"
                    onClick={() => scroll(-200)}
                    aria-label="Scroll left"
                >
                    <FaChevronLeft className="text-sm rtl:rotate-180" />
                </button>
                <button
                    className="bg-indigo-500 text-white p-1 rounded-full shadow-lg hover:bg-indigo-600"
                    onClick={() => scroll(200)}
                    aria-label="Scroll right"
                >
                    <FaChevronRight className="text-sm rtl:rotate-180" />
                </button>
            </div>
        </div>
    ) : (
        <div className="flex flex-wrap gap-2 mb-7">
            {renderChips()}
        </div>
    );
};

export default SubCategoriesFilter;
